import { getFullName } from "@vega-api/custom/utils/user";
import { type TeaserEmail } from "@vega-api/generated/types/teaserEmail";
import { TeaserEmailEmailRecipientType } from "@vega-api/generated/types/teaserEmailEmailRecipientType";
import { type TeaserEmailRecipient } from "@vega-api/generated/types/teaserEmailRecipient";
import { type TeaserEmailUserRecipient } from "@vega-api/generated/types/teaserEmailUserRecipient";
import { TeaserEmailUserRecipientType } from "@vega-api/generated/types/teaserEmailUserRecipientType";
import {
  addSelectItemFieldsToUser,
  type SearchMultiSelectItemWithUser,
} from "@vega-core/components/Form/Controls/Input/SearchUser/SearchUserMultiselect";
import { toEditorHtml } from "@vega-core/components/MinimalRichTextEditor/lexicalHtml";

export type RecipientGrant = { on: boolean; roleId: string };

export type DraftEmailFormValues = {
  to: SearchMultiSelectItemWithUser[];
  cc: SearchMultiSelectItemWithUser[];
  subject: string;
  body: string;
  grants: Record<string, RecipientGrant | undefined>;
  scheduleDate?: Date;
  scheduleTime: string;
};

export type AccessGrantRow = {
  userId: string;
  name: string;
  inCc: boolean;
  roleName: string;
  on: boolean;
};

export type AccessAlreadyRow = {
  userId: string;
  name: string;
  roleName: string;
};

export type AccessNonUserRow = {
  email: string;
};

export const recipientToItem = (
  recipient: TeaserEmailRecipient,
): SearchMultiSelectItemWithUser =>
  addSelectItemFieldsToUser(
    recipient.type === TeaserEmailUserRecipientType.USER
      ? {
          id: recipient.userId,
          fullName: getFullName(recipient.fullName),
          email: recipient.email,
        }
      : {
          id: recipient.email,
          fullName: recipient.email,
          email: recipient.email,
        },
  );

export const itemToRecipient = (
  item: SearchMultiSelectItemWithUser,
  knownUserIds: ReadonlySet<string>,
  roleId?: string,
): TeaserEmailRecipient =>
  knownUserIds.has(item.id)
    ? {
        type: TeaserEmailUserRecipientType.USER,
        teaserEmailUserRecipientDiscriminator: "TRUE",
        userId: item.id,
        email: item.email,
        fullName: { firstName: item.fullName, lastName: "" },
        ...(roleId ? { roleId } : {}),
      }
    : {
        type: TeaserEmailEmailRecipientType.EMAIL,
        teaserEmailEmailRecipientDiscriminator: "TRUE",
        email: item.email,
      };

// Combines date + "HH:mm" in the user's local timezone, then serialises to ISO.
export const composeScheduledAt = (
  date: Date | undefined,
  time: string,
): string | null => {
  if (!date || !time) return null;

  const [hours, minutes] = time.split(":").map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  const composed = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
    0,
    0,
  );

  return composed.toISOString();
};

export const parseScheduledAt = (
  iso: string | undefined,
): { date: Date | undefined; time: string } => {
  if (!iso) return { date: undefined, time: "" };

  const parsed = new Date(iso);

  if (Number.isNaN(parsed.getTime())) return { date: undefined, time: "" };

  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");

  return { date: parsed, time: `${hours}:${minutes}` };
};

type DraftEmailSource = Partial<
  Pick<
    TeaserEmail,
    "body" | "ccRecipients" | "recipients" | "scheduledAt" | "subject"
  >
>;

const isUserRecipient = (
  recipient: TeaserEmailRecipient,
): recipient is TeaserEmailUserRecipient =>
  recipient.type === TeaserEmailUserRecipientType.USER;

const grantsFromSource = (
  source: DraftEmailSource | undefined,
): Record<string, RecipientGrant> =>
  [...(source?.recipients ?? []), ...(source?.ccRecipients ?? [])]
    .filter(isUserRecipient)
    .reduce<Record<string, RecipientGrant>>((grants, recipient) => {
      if (recipient.roleId) {
        grants[recipient.userId] = { on: true, roleId: recipient.roleId };
      }

      return grants;
    }, {});

export const teaserEmailToFormValues = (
  source: DraftEmailSource | undefined,
): DraftEmailFormValues => {
  const schedule = parseScheduledAt(source?.scheduledAt);

  return {
    to: (source?.recipients ?? []).map(recipientToItem),
    cc: (source?.ccRecipients ?? []).map(recipientToItem),
    subject: source?.subject ?? "",
    body: toEditorHtml(source?.body ?? ""),
    grants: grantsFromSource(source),
    scheduleDate: schedule.date,
    scheduleTime: schedule.time,
  };
};

export const userRecipientIds = (
  source: DraftEmailSource | undefined,
): string[] =>
  [...(source?.recipients ?? []), ...(source?.ccRecipients ?? [])]
    .filter(isUserRecipient)
    .map((recipient) => recipient.userId);

export const DRAFT_AUTOSAVE_MUTATION_KEY = [
  "coinvest-deal-teaser-email-v2",
  "autosave",
] as const;

type DraftPayloadParams = {
  values: DraftEmailFormValues;
  knownUserIds: ReadonlySet<string>;
  defaultRoleId: string;
};

export const buildDraftUpdatePayload = ({
  values,
  knownUserIds,
  defaultRoleId,
}: DraftPayloadParams) => {
  const grantRoleId = (item: SearchMultiSelectItemWithUser) => {
    const grant = values.grants[item.id];

    if (!(knownUserIds.has(item.id) && grant?.on)) return undefined;

    return grant.roleId || defaultRoleId || undefined;
  };

  return {
    recipients: values.to.map((item) =>
      itemToRecipient(item, knownUserIds, grantRoleId(item)),
    ),
    ccRecipients: values.cc.map((item) =>
      itemToRecipient(item, knownUserIds, grantRoleId(item)),
    ),
    subject: values.subject.trim(),
    body: values.body,
  };
};

export const serializeDraftPayload = (params: DraftPayloadParams): string =>
  JSON.stringify(buildDraftUpdatePayload(params));

export const resolveGrant = (
  grants: DraftEmailFormValues["grants"],
  id: string,
  defaultRoleId: string,
): RecipientGrant => ({
  on: grants[id]?.on ?? false,
  roleId: grants[id]?.roleId || defaultRoleId,
});

type AccessRowsParams = {
  values: Pick<DraftEmailFormValues, "cc" | "grants" | "to">;
  knownUserIds: ReadonlySet<string>;
  activeAccessRoleById: ReadonlyMap<string, string>;
  defaultRoleId: string;
  getRoleName: (roleId: string) => string | undefined;
};

export const buildAccessRows = ({
  values,
  knownUserIds,
  activeAccessRoleById,
  defaultRoleId,
  getRoleName,
}: AccessRowsParams): {
  accessGrants: AccessGrantRow[];
  alreadyHasAccess: AccessAlreadyRow[];
  nonUsers: AccessNonUserRow[];
} => {
  const toIds = new Set(values.to.map((item) => item.id));
  const userRecipientById = new Map<string, SearchMultiSelectItemWithUser>();
  const nonUserByEmail = new Map<string, AccessNonUserRow>();

  [...values.to, ...values.cc].forEach((item) => {
    if (knownUserIds.has(item.id)) {
      userRecipientById.set(item.id, item);
    } else {
      nonUserByEmail.set(item.email, { email: item.email });
    }
  });

  const accessGrants: AccessGrantRow[] = [...userRecipientById.keys()]
    .filter((id) => !activeAccessRoleById.has(id))
    .map((id) => {
      const grant = resolveGrant(values.grants, id, defaultRoleId);

      return {
        userId: id,
        name: userRecipientById.get(id)?.fullName ?? "",
        inCc: !toIds.has(id),
        roleName: getRoleName(grant.roleId) ?? "",
        on: grant.on,
      };
    });

  const alreadyHasAccess: AccessAlreadyRow[] = [...userRecipientById.keys()]
    .filter((id) => activeAccessRoleById.has(id))
    .map((id) => ({
      userId: id,
      name: userRecipientById.get(id)?.fullName ?? "",
      roleName: activeAccessRoleById.get(id) ?? "",
    }));

  return {
    accessGrants,
    alreadyHasAccess,
    nonUsers: [...nonUserByEmail.values()],
  };
};
